import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Box,
  Alert,
  Link,
  Divider,
} from '@mui/material';
import {
  signIn,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';
import logo from '../logo.png';

interface CustomLoginProps {
  onSignIn: () => void;
}

type AuthState =
  | 'signIn'
  | 'signUp'
  | 'confirmSignUp'
  | 'newPasswordRequired'
  | 'forgotPassword'
  | 'confirmForgotPassword'
  | 'confirmSignInWithSMS';

const CustomLogin: React.FC<CustomLoginProps> = ({ onSignIn }) => {
  const [authState, setAuthState] = useState<AuthState>('signIn');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Form data
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    confirmationCode: '',
    newPassword: '',
    confirmNewPassword: '',
    mfaCode: '',
    resetCode: '',
  });

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    setError('');
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await signIn({
        username: formData.email,
        password: formData.password,
      });

      console.log('Sign in result:', result);

      // Check if MFA is required
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
        setSuccess('Please enter the SMS code sent to your phone.');
        setAuthState('confirmSignInWithSMS');
        return;
      }

      // Check if the user needs to set a new password
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setSuccess('Please set a new password to complete sign in.');
        setAuthState('newPasswordRequired');
        return;
      }

      // If sign in is complete, call the parent's onSignIn callback
      if (result.isSignedIn) {
        onSignIn();
      }
    } catch (err: any) {
      console.error('Sign in error:', err);
      setError(err.message || 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await signUp({
        username: formData.email,
        password: formData.password,
        options: {
          userAttributes: {
            email: formData.email,
          },
        },
      });
      setSuccess('Account created! Please check your email for the confirmation code.');
      setAuthState('confirmSignUp');
    } catch (err: any) {
      setError(err.message || 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await confirmSignUp({
        username: formData.email,
        confirmationCode: formData.confirmationCode,
      });
      setSuccess('Account confirmed! You can now sign in.');
      setAuthState('signIn');
      setFormData((prev) => ({ ...prev, confirmationCode: '' }));
    } catch (err: any) {
      setError(err.message || 'Confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');

    try {
      await resendSignUpCode({ username: formData.email });
      setSuccess('Confirmation code resent to your email.');
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordRequired = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await confirmSignIn({
        challengeResponse: formData.newPassword,
      });

      console.log('New password set result:', result);

      // Check if MFA is required after password change
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
        setSuccess('Password updated! Please enter the SMS code sent to your phone.');
        setAuthState('confirmSignInWithSMS');
        return;
      }

      // If sign in is complete, call the parent's onSignIn callback
      if (result.isSignedIn) {
        setSuccess('Password updated successfully! Signing you in...');
        onSignIn();
      }
    } catch (err: any) {
      console.error('New password error:', err);
      setError(err.message || 'Failed to set new password');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSMSCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await confirmSignIn({
        challengeResponse: formData.mfaCode,
      });

      console.log('MFA confirmation result:', result);

      if (result.isSignedIn) {
        setSuccess('MFA verified! Signing you in...');
        onSignIn();
      }
    } catch (err: any) {
      console.error('MFA confirmation error:', err);
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await resetPassword({ username: formData.email });
      setSuccess('Password reset code sent to your email.');
      setAuthState('confirmForgotPassword');
    } catch (err: any) {
      console.error('Forgot password error:', err);
      setError(err.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await confirmResetPassword({
        username: formData.email,
        confirmationCode: formData.resetCode,
        newPassword: formData.newPassword,
      });
      setSuccess('Password reset successfully! You can now sign in.');
      setAuthState('signIn');
      setFormData((prev) => ({
        ...prev,
        resetCode: '',
        newPassword: '',
        confirmNewPassword: '',
        password: '',
      }));
    } catch (err: any) {
      console.error('Confirm reset password error:', err);
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const renderSignInForm = () => (
    <Box component="form" onSubmit={handleSignIn}>
      <TextField
        fullWidth
        label="Email"
        type="email"
        value={formData.email}
        onChange={handleInputChange('email')}
        margin="normal"
        required
        autoComplete="email"
      />
      <TextField
        fullWidth
        label="Password"
        type="password"
        value={formData.password}
        onChange={handleInputChange('password')}
        margin="normal"
        required
        autoComplete="current-password"
      />
      <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 3, mb: 2 }}>
        {loading ? 'Signing In...' : 'Sign In'}
      </Button>
      <Box textAlign="center">
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => setAuthState('forgotPassword')}
          sx={{ textDecoration: 'none' }}
        >
          Forgot Password?
        </Link>
      </Box>
      {/*  // TODO: Disabled sign-up link for now
      <Box textAlign="center">
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => setAuthState('signUp')}
          sx={{ textDecoration: 'none' }}
        >
          Don&apos;t have an account? Sign Up
        </Link>
      </Box> */}
    </Box>
  );

  const renderSignUpForm = () => (
    <Box component="form" onSubmit={handleSignUp}>
      <TextField
        fullWidth
        label="Email"
        type="email"
        value={formData.email}
        onChange={handleInputChange('email')}
        margin="normal"
        required
        autoComplete="email"
      />
      <TextField
        fullWidth
        label="Password"
        type="password"
        value={formData.password}
        onChange={handleInputChange('password')}
        margin="normal"
        required
        autoComplete="new-password"
        helperText="Password must be at least 8 characters with uppercase, lowercase, and numbers"
      />
      <TextField
        fullWidth
        label="Confirm Password"
        type="password"
        value={formData.confirmPassword}
        onChange={handleInputChange('confirmPassword')}
        margin="normal"
        required
        autoComplete="new-password"
      />
      <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 3, mb: 2 }}>
        {loading ? 'Creating Account...' : 'Sign Up'}
      </Button>
      <Box textAlign="center">
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => setAuthState('signIn')}
          sx={{ textDecoration: 'none' }}
        >
          Already have an account? Sign In
        </Link>
      </Box>
    </Box>
  );

  const renderConfirmSignUpForm = () => (
    <Box component="form" onSubmit={handleConfirmSignUp}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Please enter the confirmation code sent to {formData.email}
      </Typography>
      <TextField
        fullWidth
        label="Confirmation Code"
        value={formData.confirmationCode}
        onChange={handleInputChange('confirmationCode')}
        margin="normal"
        required
        autoComplete="one-time-code"
      />
      <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 3, mb: 2 }}>
        {loading ? 'Confirming...' : 'Confirm Account'}
      </Button>
      <Box textAlign="center">
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={handleResendCode}
          disabled={loading}
          sx={{ textDecoration: 'none', mr: 2 }}
        >
          Resend Code
        </Link>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => setAuthState('signIn')}
          sx={{ textDecoration: 'none' }}
        >
          Back to Sign In
        </Link>
      </Box>
    </Box>
  );

  const renderNewPasswordRequiredForm = () => (
    <Box component="form" onSubmit={handleNewPasswordRequired}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        For security, you must set a new password before continuing.
      </Typography>
      <TextField
        fullWidth
        label="New Password"
        type="password"
        value={formData.newPassword}
        onChange={handleInputChange('newPassword')}
        margin="normal"
        required
        autoComplete="new-password"
        helperText="Password must be at least 8 characters with uppercase, lowercase, and numbers"
      />
      <TextField
        fullWidth
        label="Confirm New Password"
        type="password"
        value={formData.confirmNewPassword}
        onChange={handleInputChange('confirmNewPassword')}
        margin="normal"
        required
        autoComplete="new-password"
      />
      <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 3, mb: 2 }}>
        {loading ? 'Setting Password...' : 'Set New Password'}
      </Button>
    </Box>
  );

  const renderConfirmSMSForm = () => (
    <Box component="form" onSubmit={handleConfirmSMSCode}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Please enter the verification code sent to your phone via SMS.
      </Typography>
      <TextField
        fullWidth
        label="Verification Code"
        value={formData.mfaCode}
        onChange={handleInputChange('mfaCode')}
        margin="normal"
        required
        autoComplete="one-time-code"
        inputProps={{ maxLength: 6 }}
      />
      <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 3, mb: 2 }}>
        {loading ? 'Verifying...' : 'Verify Code'}
      </Button>
    </Box>
  );

  const renderForgotPasswordForm = () => (
    <Box component="form" onSubmit={handleForgotPassword}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter your email address and we&apos;ll send you a password reset code.
      </Typography>
      <TextField
        fullWidth
        label="Email"
        type="email"
        value={formData.email}
        onChange={handleInputChange('email')}
        margin="normal"
        required
        autoComplete="email"
      />
      <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 3, mb: 2 }}>
        {loading ? 'Sending Code...' : 'Send Reset Code'}
      </Button>
      <Box textAlign="center">
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => setAuthState('signIn')}
          sx={{ textDecoration: 'none' }}
        >
          Back to Sign In
        </Link>
      </Box>
    </Box>
  );

  const renderConfirmResetPasswordForm = () => (
    <Box component="form" onSubmit={handleConfirmResetPassword}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter the code sent to {formData.email} and your new password.
      </Typography>
      <TextField
        fullWidth
        label="Reset Code"
        value={formData.resetCode}
        onChange={handleInputChange('resetCode')}
        margin="normal"
        required
        autoComplete="one-time-code"
      />
      <TextField
        fullWidth
        label="New Password"
        type="password"
        value={formData.newPassword}
        onChange={handleInputChange('newPassword')}
        margin="normal"
        required
        autoComplete="new-password"
        helperText="Password must be at least 8 characters with uppercase, lowercase, and numbers"
      />
      <TextField
        fullWidth
        label="Confirm New Password"
        type="password"
        value={formData.confirmNewPassword}
        onChange={handleInputChange('confirmNewPassword')}
        margin="normal"
        required
        autoComplete="new-password"
      />
      <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 3, mb: 2 }}>
        {loading ? 'Resetting Password...' : 'Reset Password'}
      </Button>
      <Box textAlign="center">
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => setAuthState('signIn')}
          sx={{ textDecoration: 'none' }}
        >
          Back to Sign In
        </Link>
      </Box>
    </Box>
  );

  const getTitle = () => {
    switch (authState) {
      case 'signUp':
        return 'Create Account';
      case 'confirmSignUp':
        return 'Confirm Account';
      case 'newPasswordRequired':
        return 'Set New Password';
      case 'confirmSignInWithSMS':
        return 'Verify SMS Code';
      case 'forgotPassword':
        return 'Reset Password';
      case 'confirmForgotPassword':
        return 'Confirm Reset';
      default:
        return 'Sign In';
    }
  };

  const getCurrentForm = () => {
    switch (authState) {
      case 'signUp':
        return renderSignUpForm();
      case 'confirmSignUp':
        return renderConfirmSignUpForm();
      case 'newPasswordRequired':
        return renderNewPasswordRequiredForm();
      case 'confirmSignInWithSMS':
        return renderConfirmSMSForm();
      case 'forgotPassword':
        return renderForgotPasswordForm();
      case 'confirmForgotPassword':
        return renderConfirmResetPasswordForm();
      default:
        return renderSignInForm();
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{ py: 8, minHeight: '100vh', display: 'flex', alignItems: 'center' }}
    >
      <Paper variant="outlined" sx={{ p: 4, width: '100%', boxShadow: 1 }}>
        {/* Header with Logo */}
        <Stack alignItems="center" spacing={2} sx={{ mb: 4 }}>
          <img src={logo} alt="Monsoon Aquatics" style={{ height: 60 }} />
          <Typography variant="h4" sx={{ color: 'text.primary' }}>
            Coral Tagging Application
          </Typography>
          <Divider sx={{ width: '100%' }} />
          <Typography variant="h5" sx={{ color: 'text.secondary' }}>
            {getTitle()}
          </Typography>
        </Stack>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Current Form */}
        {getCurrentForm()}
      </Paper>
    </Container>
  );
};

export default CustomLogin;
